/**
 * Whitelist Store
 *
 * Manages whitelist sync and filtering state.
 * CRITICAL: This is the client-side layer of whitelist enforcement.
 */

import { create } from 'zustand';
import type {
  ArtistWhitelistEntry,
  ContentFilterConfig,
  WhitelistVersion,
} from '@zemer/shared';
import { db } from '@/db/database';
import { api } from '@/api/client';

interface WhitelistState {
  // Whitelist state
  version: WhitelistVersion | null;
  entries: ArtistWhitelistEntry[];
  isLoading: boolean;
  error: string | null;
  lastSyncAt: number | null;

  // Filter config
  filterConfig: ContentFilterConfig;

  // Actions
  syncWhitelist: () => Promise<void>;
  loadEntries: () => Promise<void>;
  checkForUpdates: () => Promise<boolean>;
  setFilterConfig: (config: Partial<ContentFilterConfig>) => void;
  isArtistAllowed: (artistId: string) => Promise<boolean>;
}

const DEFAULT_CONFIG: ContentFilterConfig = {
  filtersEnabled: false,
  allowFemaleSingers: true,
  allowChasidish: true,
  allowGenZ: true,
  allowKids: true,
  allowKidZone: true,
  allowIsraeli: true,
};

export const useWhitelistStore = create<WhitelistState>((set, get) => ({
  version: null,
  entries: [],
  isLoading: false,
  error: null,
  lastSyncAt: null,
  filterConfig: DEFAULT_CONFIG,

  // Load entries from IndexedDB into state
  loadEntries: async () => {
    try {
      const entries = await db.artistWhitelist.toArray();
      set({ entries });
      console.log(`[Whitelist] Loaded ${entries.length} entries from IndexedDB`);
    } catch (error) {
      console.error('[Whitelist] Failed to load entries:', error);
    }
  },

  // Sync whitelist from server
  syncWhitelist: async () => {
    set({ isLoading: true, error: null });

    try {
      // Fetch full whitelist directly
      const response = await api.get<{
        version: WhitelistVersion;
        entries: ArtistWhitelistEntry[];
      }>('/whitelist');

      if (response.data) {
        const { version, entries } = response.data;

        console.log(`[Whitelist] Fetched ${entries.length} entries from server`);

        // Store in IndexedDB for offline use
        try {
          await db.transaction('rw', db.artistWhitelist, async () => {
            await db.artistWhitelist.clear();
            await db.artistWhitelist.bulkAdd(entries);
          });
        } catch (dbError) {
          console.warn('[Whitelist] IndexedDB storage failed:', dbError);
        }

        // Always set entries directly from response
        set({
          version,
          entries,
          lastSyncAt: Date.now(),
          isLoading: false,
        });
      } else {
        // Try loading from IndexedDB if server fails
        await get().loadEntries();
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('[Whitelist] Sync failed:', error);
      // Try loading from IndexedDB as fallback
      await get().loadEntries();
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sync failed',
      });
    }
  },

  // Check if whitelist needs updating (always returns true for now)
  checkForUpdates: async () => {
    return true;
  },

  // Update filter config
  setFilterConfig: (config) => {
    set((state) => ({
      filterConfig: { ...state.filterConfig, ...config },
    }));
  },

  // Check if an artist is allowed (from IndexedDB)
  isArtistAllowed: async (artistId) => {
    const { filterConfig } = get();

    // If filters disabled, allow all whitelisted artists
    const entry = await db.artistWhitelist.get(artistId);

    if (!entry) return false;

    if (!filterConfig.filtersEnabled) return true;

    // Apply all category filters (matching Android app logic)
    if (!filterConfig.allowFemaleSingers && entry.isFemale) return false;
    if (!filterConfig.allowChasidish && entry.isChasid) return false;
    if (!filterConfig.allowGenZ && entry.isGenZ) return false;
    if (!filterConfig.allowKids && entry.isKids) return false;
    if (!filterConfig.allowKidZone && entry.isKidZone) return false;
    if (!filterConfig.allowIsraeli && entry.isIsraeli) return false;

    return true;
  },
}));
